import type { SyncRequest } from '../../sync/syncRuntime'
import { activeWorkspace, assertWorkspaceActive } from '../../data/workspace'
import type { ContextsAccountAdapter, ContextsAccountWorkspace, ContextsRecoverySource } from './contract'
import { legacyContextsAccountAdapter } from './legacyAdapter'

function requireActiveAccount(): ContextsAccountWorkspace {
  assertWorkspaceActive()
  const workspace = activeWorkspace()
  if (!workspace?.owner) throw new Error('Sign in to access this account’s Contexts.')
  return { ...workspace, owner: workspace.owner }
}
function assertSameWorkspace(expected: ContextsAccountWorkspace): void {
  const current = requireActiveAccount()
  if (current.databaseName !== expected.databaseName || current.owner !== expected.owner
    || current.scope.project !== expected.scope.project || current.scope.environment !== expected.scope.environment) {
    throw new Error('The active account changed. Contexts operation stopped.')
  }
}

/** One composition seam. Callers depend only on lifecycle behavior, never the legacy model. */
export function createContextsAccountIntegration(adapter: ContextsAccountAdapter) {
  return {
    async inspect() {
      const workspace = requireActiveAccount()
      const result = await adapter.inspect(workspace)
      assertSameWorkspace(workspace)
      if (!Number.isSafeInteger(result.pendingChanges) || result.pendingChanges < 0) throw new Error('Contexts changes could not be checked.')
      return result
    },
    async captureRecovery() {
      const workspace = requireActiveAccount()
      const result = await adapter.captureRecovery(workspace)
      assertSameWorkspace(workspace)
      return result
    },
    async prepareRestore(source: ContextsRecoverySource) {
      const workspace = requireActiveAccount()
      const restore = await adapter.prepareRestore(workspace, source)
      assertSameWorkspace(workspace)
      return async () => { assertSameWorkspace(workspace); await restore(); assertSameWorkspace(workspace) }
    },
    isAuthoredRecord: (store: string, key: string) => adapter.isAuthoredRecord(store, key),
    // Removal intentionally accepts a retired workspace, already validated/authorized by its caller.
    removeOfflineContent: (workspace: ContextsAccountWorkspace) => adapter.removeOfflineContent(workspace),
    async synchronizeForSignOut(host: { runSharedSync(request: SyncRequest): Promise<unknown> }) {
      const workspace = requireActiveAccount()
      await adapter.synchronize(workspace, {
        async synchronizeCore(additionalDomains = []) {
          assertSameWorkspace(workspace)
          const result = await host.runSharedSync({ push: true, domains: [...new Set<SyncRequest['domains'][number]>(['notes', 'settings', 'assets', ...additionalDomains])] })
          assertSameWorkspace(workspace)
          return result
        },
      })
      assertSameWorkspace(workspace)
    },
  }
}
export const contextsAccount = createContextsAccountIntegration(legacyContextsAccountAdapter)
